import SaveIcon from '@mui/icons-material/Save';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useGetPieceQuery, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';

import { ActionComponentProps } from '../../index';
import { CollapsibleOptions } from './CollapsibleOptions';
import { loadingDialogSX } from './styles';

export const FinishMail = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);
  const [updatePieces] = useUpdatePiecesMutation();

  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const [trackingNumber, setTrackingNumber] = useState('');
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(false);
  const [comment, setComment] = useState('');
  const [notifyMailroom, setNotifyMailroom] = useState(true);

  const handleChangeTrackingNumber = (e: React.ChangeEvent<HTMLInputElement>) => setTrackingNumber(e.target.value);
  const { data, isLoading } = useGetPieceQuery(mailpieces[0].id);

  const hasNoMailTo = !isLoading && !data?.mailTo;

  useEffect(() => {
    if (hasNoMailTo) {
      onActionFinish();
    }
  }, [hasNoMailTo, onActionFinish]);

  const confirmFinishMail = async () => {
    try {
      setActionInProgress(true);

      await updatePieces({
        pieces: mailpieces.map((piece) => ({ id: piece.id })),
        action: PieceAction.FinishMail,
        trackingNumber,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      setActionInProgress(false);
      onActionFinish();
    } catch (error) {
      setActionInProgress(false);
    }
  };

  if (isLoading) {
    return (
      <Dialog maxWidth="sm" open={true} onClose={onActionFinish}>
        <Box sx={loadingDialogSX}>
          <CircularProgress size={40} />
        </Box>
      </Dialog>
    );
  }

  if (hasNoMailTo || !data?.mailTo) {
    return null;
  }

  const { addressLine1, addressLine2, administrativeArea, locality, postalCode, countryCode } = data.mailTo;

  return (
    <Dialog maxWidth="sm" open={actionStarted} onClose={actionInProgress ? () => {} : onActionFinish}>
      <DialogTitle sx={{ p: 3 }}>{mailpieces.length === 1 ? 'Mail item' : 'Mail selected items'}</DialogTitle>
      <DialogContent>
        <Stack direction="column" spacing={3}>
          <Typography variant="body1" color="text.secondary">
            {mailpieces.length === 1 ? (
              <>Please mail this item to the following address, and enter the tracking number if applicable.</>
            ) : (
              <>
                Please mail <strong>{mailpieces.length}</strong> item(s) to the following address, and enter the
                tracking number if applicable.
              </>
            )}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            <strong>
              {`${mailpieces[0].recipient?.displayName || ''}`}
              <br />
              {addressLine1}
              <br />
              {!!addressLine2 && (
                <>
                  {addressLine2}
                  <br />
                </>
              )}
              {`${locality} ${administrativeArea} ${postalCode}`}
              <br />
              {countryCode}
            </strong>
          </Typography>
          <TextField
            value={trackingNumber}
            onChange={handleChangeTrackingNumber}
            placeholder="Tracking Number"
            fullWidth
          />
        </Stack>

        <CollapsibleOptions>
          <TextField
            sx={{ width: '100%', mt: 1, mb: 2 }}
            label="Optional Comment"
            multiline
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <NotifyMailroomSwitch
            checked={notifyMailroom}
            onChange={() => setNotifyMailroom(!notifyMailroom)}
            label="Notify mailroom users about comment"
          />
          {isAdmin && (
            <Box sx={{ mt: 1 }}>
              <NotifyMailroomSwitch
                checked={skipNotifyRecipient}
                onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
                label="Skip notification to recipient about completed request"
              />
            </Box>
          )}
        </CollapsibleOptions>
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onActionFinish} disabled={actionInProgress}>
          Cancel
        </Button>
        <Button
          onClick={confirmFinishMail}
          loading={actionInProgress}
          startIcon={actionInProgress && <SaveIcon />}
          loadingPosition={actionInProgress ? 'start' : undefined}
          variant="contained"
          color="primary"
        >
          Complete
        </Button>
      </DialogActions>
    </Dialog>
  );
};
